import { ArrowRight } from 'lucide-react';
import type { ComponentProps, ReactNode } from 'react';

import { accentColor, AUTH_ACCENT_HUE } from '@/components/auth/auth-config';
import { Brandmark } from '@/components/auth/auth-icons';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';

/**
 * Card chrome used by the /login screen (and any future public auth screen): container,
 * corner tag, brand row, an Auth0 hand-off note above the bottom strip,
 * and the strip itself. The page supplies its body (heading + action)
 * as children and the left side of the strip; the right side is always
 * the support link.
 */
export function AuthCard({
    note,
    footerLeft,
    children,
}: {
    note: string;
    footerLeft: ReactNode;
    children: ReactNode;
}) {
    return (
        <div className="relative z-10 w-full max-w-[420px] rounded-2xl border border-border bg-card px-9 pt-9 pb-8 shadow-[0_30px_60px_-24px_rgba(15,23,35,0.18),0_2px_6px_rgba(15,23,35,0.04)] dark:shadow-[0_30px_60px_-24px_rgba(0,0,0,0.6)]">
            {/* Corner tag */}
            <div className="absolute top-4 right-[18px] font-mono text-[10px] tracking-[0.14em] text-muted-foreground/80">
                SECURE · TLS 1.3
            </div>

            {/* Brand row */}
            <div className="mb-7 flex items-center gap-2.5">
                <Brandmark className="h-[26px] w-[26px] text-[color:var(--auth-accent)]" />
                <div className="flex flex-col leading-[1.1]">
                    <span className="text-[17px] font-semibold tracking-[-0.01em] text-foreground">
                        ESCloud
                    </span>
                    <span className="font-mono text-[10px] tracking-[0.12em] text-muted-foreground/80">
                        v{__APP_VERSION__} · CLINICAL
                    </span>
                </div>
            </div>

            {children}

            <p className="mt-4 text-center font-mono text-[11px] leading-[1.5] tracking-[0.08em] text-muted-foreground/80">
                {note}
            </p>

            <div className="mt-6 flex items-center justify-between border-t border-border pt-[18px] text-xs text-muted-foreground/80">
                {footerLeft}
                <a href="mailto:support@therenva.com" className="text-muted-foreground hover:text-foreground">
                    Need help?
                </a>
            </div>
        </div>
    );
}

/** The card shown while the Auth0 SDK is still resolving the session. */
export function AuthCardConnecting() {
    return (
        <div className="relative z-10 flex w-full max-w-[420px] flex-col items-center gap-4 rounded-2xl border border-border bg-card px-9 py-10 shadow-xl">
            <Spinner className="h-8 w-8 text-primary" />
            <p className="text-sm text-muted-foreground">Connecting…</p>
        </div>
    );
}

/** Heading + one-line pitch at the top of the card body. */
export function AuthCardIntro({ title, description }: { title: string; description: string }) {
    return (
        <div className="mb-8">
            <h2 className="mb-2 text-[22px] font-medium tracking-[-0.015em] text-foreground">{title}</h2>
            <p className="text-sm leading-[1.55] text-muted-foreground">{description}</p>
        </div>
    );
}

/** The gradient call-to-action button with the sliding arrow. */
export function AuthAccentButton({ children, ...props }: ComponentProps<typeof Button>) {
    return (
        <Button
            {...props}
            size="lg"
            className="group w-full justify-center gap-2.5 border-0 py-[14px] text-[15px] font-semibold tracking-[-0.005em] text-white shadow-[0_10px_24px_-8px_var(--auth-button-shadow)] hover:brightness-105 active:translate-y-px"
            style={{
                background: `linear-gradient(180deg, ${accentColor(60, 0.17, 1, AUTH_ACCENT_HUE)} 0%, ${accentColor(52, 0.17, 1, AUTH_ACCENT_HUE)} 100%)`,
                ['--auth-button-shadow' as string]: accentColor(55, 0.17, 0.45, AUTH_ACCENT_HUE),
            }}
        >
            <span>{children}</span>
            <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
        </Button>
    );
}
